import { LegalLayout } from "@/components/legal/LegalLayout";

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="February 14, 2026">
      <section>
        <h2 id="information-we-collect">1. Information We Collect</h2>
        <p>
          We collect information you provide directly to us when you create an account, use the Service, make a purchase, or communicate with us. This includes:
        </p>
        <ul>
          <li><strong>Account Information:</strong> Your name, email address, and password when you register for an account.</li>
          <li><strong>Profile Information:</strong> Optional information such as your profile picture, organization name, and job title.</li>
          <li><strong>Payment Information:</strong> Billing address and payment method details, processed securely through our third-party payment processor. We do not store full credit card numbers on our servers.</li>
          <li><strong>Pipeline Data:</strong> The pipeline workflows you create, including node configurations, AI model parameters, prompts, and any input data you provide to your pipelines.</li>
          <li><strong>AI-Generated Content:</strong> Outputs produced by your pipeline executions, including text, images, video, and audio files.</li>
          <li><strong>Usage Data:</strong> Information about how you interact with the Service, including features used, pipelines executed, pages visited, and time spent on the platform.</li>
          <li><strong>Device and Technical Data:</strong> IP address, browser type, operating system, device identifiers, and referring URLs collected automatically when you access the Service.</li>
          <li><strong>Communications:</strong> Records of your communications with us, including support requests and feedback.</li>
        </ul>
      </section>

      <section>
        <h2 id="how-we-use">2. How We Use Your Information</h2>
        <p>
          We use the information we collect for the following purposes:
        </p>
        <ul>
          <li>To provide, maintain, and improve the Service, including executing your pipeline workflows and delivering AI-generated outputs.</li>
          <li>To process transactions and manage your subscription and credit balance.</li>
          <li>To send you technical notices, security alerts, and support and administrative messages.</li>
          <li>To respond to your comments, questions, and customer service requests.</li>
          <li>To analyze usage patterns and trends to improve the user experience and develop new features.</li>
          <li>To detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of Rayloop AI and others.</li>
          <li>To personalize your experience, including suggesting pipeline templates and AI model configurations relevant to your usage patterns.</li>
          <li>To send promotional communications, such as information about products, services, and events offered by Rayloop AI, where permitted by law and in accordance with your preferences.</li>
        </ul>
      </section>

      <section>
        <h2 id="data-sharing">3. Data Sharing & Third Parties</h2>
        <p>
          We do not sell your personal information. We may share your information with the following categories of third parties:
        </p>
        <h3>AI Model Providers</h3>
        <p>
          When you execute pipeline workflows, the input data and prompts within your pipeline nodes are transmitted to the respective third-party AI model providers (such as OpenAI, Anthropic, Stability AI, Replicate, and others) necessary to generate the requested outputs. Each AI provider processes this data in accordance with their own privacy policies and terms of service. We encourage you to review the privacy practices of any AI providers used within your pipelines.
        </p>
        <h3>Payment Processors</h3>
        <p>
          We use third-party payment processors (such as Stripe) to handle subscription billing and credit purchases. These processors receive the payment information necessary to complete your transactions and are bound by their own privacy policies and PCI-DSS compliance requirements.
        </p>
        <h3>Service Providers</h3>
        <p>
          We engage third-party companies and individuals to perform services on our behalf, including cloud hosting, analytics, customer support, and email delivery. These service providers have access to your information only as needed to perform their functions and are contractually obligated to protect your data.
        </p>
        <h3>Legal and Safety</h3>
        <p>
          We may disclose your information if required to do so by law, or if we believe in good faith that such action is necessary to comply with legal obligations, protect and defend our rights or property, prevent fraud, or protect the personal safety of users or the public.
        </p>
      </section>

      <section>
        <h2 id="data-storage">4. Data Storage & Security</h2>
        <p>
          We implement industry-standard technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit and at rest, regular security assessments, and access controls limiting employee access to personal data.
        </p>
        <p>
          Your pipeline workflows and AI-generated outputs are stored on secure cloud infrastructure. While we strive to use commercially acceptable means to protect your data, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.
        </p>
        <p>
          We retain your personal information for as long as your account is active or as needed to provide you the Service. If you delete your account, we will delete or anonymize your personal information within ninety (90) days, except where retention is required by law or for legitimate business purposes.
        </p>
      </section>

      <section>
        <h2 id="cookies">5. Cookies</h2>
        <p>
          We use cookies and similar tracking technologies to collect and track information about your use of the Service and to improve your experience. For detailed information about the cookies we use and how to manage your cookie preferences, please see our <a href="/cookies">Cookie Policy</a>.
        </p>
      </section>

      <section>
        <h2 id="your-rights">6. Your Rights</h2>
        <p>
          Depending on your location, you may have certain rights regarding your personal information, including:
        </p>
        <ul>
          <li><strong>Access:</strong> The right to request a copy of the personal information we hold about you.</li>
          <li><strong>Correction:</strong> The right to request that we correct inaccurate or incomplete personal information.</li>
          <li><strong>Deletion:</strong> The right to request that we delete your personal information, subject to certain exceptions.</li>
          <li><strong>Portability:</strong> The right to receive your personal information in a structured, commonly used, and machine-readable format.</li>
          <li><strong>Objection:</strong> The right to object to certain processing of your personal information, including processing for direct marketing purposes.</li>
          <li><strong>Restriction:</strong> The right to request that we restrict certain processing of your personal information.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at hello@rayloop.ai. We will respond to your request within thirty (30) days, or as required by applicable law.
        </p>
      </section>

      <section>
        <h2 id="children">7. Children's Privacy</h2>
        <p>
          The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly. If you believe that we may have collected information from a child under 18, please contact us at hello@rayloop.ai.
        </p>
      </section>

      <section>
        <h2 id="international">8. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence, including the United States, where our servers and central operations are located. These countries may have data protection laws that are different from the laws of your country.
        </p>
        <p>
          When we transfer personal information from the European Economic Area (EEA), the United Kingdom, or Switzerland, we rely on appropriate legal mechanisms such as Standard Contractual Clauses approved by the European Commission, or other lawful transfer mechanisms, to ensure that your data is protected in accordance with applicable data protection laws.
        </p>
      </section>

      <section>
        <h2 id="changes">9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date above. For material changes, we will also provide notice through the Service or via email.
        </p>
        <p>
          We encourage you to review this Privacy Policy periodically for any changes. Your continued use of the Service after any modifications to this Privacy Policy constitutes your acceptance of such modifications.
        </p>
      </section>

      <section>
        <h2 id="contact">10. Contact Us</h2>
        <p>
          If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
        </p>
        <p>
          Rayloop AI, Inc.<br />
          Email: hello@rayloop.ai<br />
          Website: <a href="https://rayloop.ai">rayloop.ai</a>
        </p>
        <p>
          For EEA residents, you also have the right to lodge a complaint with your local data protection authority if you believe our processing of your personal information violates applicable law.
        </p>
      </section>
    </LegalLayout>
  );
}
